import React, { useState, useEffect } from 'react'
import {
  RiFilePaper2Line,
  RiLinksLine,
  RiCalendarLine,
  RiAttachmentLine,
  RiDownloadLine,
  RiFileTextLine,
  RiFileExcelLine,
  RiFileWordLine,
  RiFilePdfLine,
  RiImageLine,
  RiHistoryLine,
  RiEyeLine,
  RiFileList3Line,
} from 'react-icons/ri'
import { HiOutlineBuildingOffice, HiClock, HiEye, HiCheck } from 'react-icons/hi2'
import { FaLock, FaBook } from 'react-icons/fa'
import type { Processo, Setor } from '../../../../models'
import { UserAvatar } from '../../../../components/UserAvatar'
import { HorizontalScrollNavigator } from '../../../../components/ScrollNavigator'
import { format } from 'date-fns'
import { processoService } from '../../../../services/processo'
import { authService } from '../../../../services/auth'
import { api } from '../../../../services/axios'
import { processRichTextForDisplay } from '../../../../utils/formatter'
import { usePermissions } from '../../hooks/usePermissions'
import { useAuth } from '../../../../contexts/AuthContext'

interface ProcessoViewCardProps {
  processo: Processo
  onNavigateToSetor?: (setor: Setor) => void
}

// Tipo para dados de acesso retornados pela API
type AcessoData = {
  id: number
  processo_fk: number
  usuario_fk: number
  usuario?: {
    id: number
    name: string
    email: string
    avatar?: string
  }
  created_at: string
  updated_at: string
}

// Tipo para autorizadores que agora são setores
type SetorAutorizador = {
  id: number
  nome_setor: string
  equipe?: boolean
  chefe_setor_fk?: number | null
  active?: boolean
  pivot?: {
    processo_fk: number
    autorizador_fk: number
    ordem: number
    active: boolean
    created_at?: string
    updated_at?: string
  }
}

type FixedRoleKey =
  | 'gestor_demandante'
  | 'executivo_demandante'
  | 'executivo_demandado'
  | 'gestor_demandado'

const FIXED_ROLE_META: Record<FixedRoleKey, { label: string; initials: string; description: string; bubbleClass: string }> = {
  gestor_demandante: {
    label: 'Gestor da Unidade Demandante',
    initials: 'GD',
    description: '',
    bubbleClass:
      'bg-gradient-to-br from-blue-500 to-blue-600 dark:from-blue-600 dark:to-blue-700 border border-blue-300 dark:border-blue-600',
  },
  executivo_demandante: {
    label: 'Executivo da Unidade Demandante',
    initials: 'ED',
    description: '',
    bubbleClass:
      'bg-gradient-to-br from-indigo-500 to-indigo-600 dark:from-indigo-600 dark:to-indigo-700 border border-indigo-300 dark:border-indigo-600',
  },
  executivo_demandado: {
    label: 'Executivo da Unidade Demandada',
    initials: 'ED',
    description: '',
    bubbleClass:
      'bg-gradient-to-br from-teal-500 to-teal-600 dark:from-teal-600 dark:to-teal-700 border border-teal-300 dark:border-teal-600',
  },
  gestor_demandado: {
    label: 'Gestor da Unidade Demandada',
    initials: 'GD',
    description: '',
    bubbleClass:
      'bg-gradient-to-br from-emerald-500 to-emerald-600 dark:from-emerald-600 dark:to-emerald-700 border border-emerald-300 dark:border-emerald-600',
  },
}

type VisualAutorizacaoItem =
  | {
      id: string
      type: 'fixed'
      role: FixedRoleKey
      ordemDisplay: number | null
    }
  | {
      id: string
      type: 'setor'
      setor: SetorAutorizador
      ordemDisplay: number | null
    }

const PriorityBadge = ({
  type,
  days,
}: {
  type: 'baixa' | 'media' | 'alta' | 'urgente'
  days: number
}) => {
  const config = {
    baixa: {
      color: 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-400',
      label: 'Baixa',
      dot: 'bg-blue-500',
    },
    media: {
      color: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-400',
      label: 'Média',
      dot: 'bg-yellow-500',
    },
    alta: {
      color: 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-400',
      label: 'Alta',
      dot: 'bg-orange-500',
    },
    urgente: {
      color: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-400',
      label: 'Urgente',
      dot: 'bg-red-500',
    },
  }

  const { color, label, dot } = config[type]

  return (
    <div
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium ${color}`}
    >
      <div className={`w-2 h-2 rounded-full ${dot}`}></div>
      <span>{label}</span>
      <span className='font-semibold'>{days} dias</span>
    </div>
  )
}

const getFileIcon = (fileName: string) => {
  const extension = fileName.split('.').pop()?.toLowerCase() || ''

  if (['pdf'].includes(extension)) {
    return <RiFilePdfLine className='w-5 h-5 text-red-600 dark:text-red-400' />
  }
  if (['doc', 'docx'].includes(extension)) {
    return <RiFileWordLine className='w-5 h-5 text-blue-600 dark:text-blue-400' />
  }
  if (['xls', 'xlsx', 'csv'].includes(extension)) {
    return <RiFileExcelLine className='w-5 h-5 text-green-600 dark:text-green-400' />
  }
  if (['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp'].includes(extension)) {
    return <RiImageLine className='w-5 h-5 text-pink-600 dark:text-pink-400' />
  }
  if (['txt', 'md', 'log'].includes(extension)) {
    return <RiFileTextLine className='w-5 h-5 text-gray-600 dark:text-gray-400' />
  }

  return <RiAttachmentLine className='w-5 h-5 text-purple-600 dark:text-purple-400' />
}

const getFileIconBgColor = (fileName: string) => {
  const extension = fileName.split('.').pop()?.toLowerCase() || ''

  if (['pdf'].includes(extension)) return 'bg-red-100 dark:bg-red-900/40'
  if (['doc', 'docx'].includes(extension)) return 'bg-blue-100 dark:bg-blue-900/40'
  if (['xls', 'xlsx', 'csv'].includes(extension)) return 'bg-green-100 dark:bg-green-900/40'
  if (['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp'].includes(extension))
    return 'bg-pink-100 dark:bg-pink-900/40'
  if (['txt', 'md', 'log'].includes(extension)) return 'bg-gray-100 dark:bg-gray-900/40'

  return 'bg-purple-100 dark:bg-purple-900/40'
}

export const ProcessoViewCard: React.FC<ProcessoViewCardProps> = ({
  processo,
  onNavigateToSetor,
}) => {
  const { user: currentUser } = useAuth()
  const permissions = usePermissions({ currentUser })
  
  const [versions, setVersions] = useState<Processo[]>([])
  const [loadingVersions, setLoadingVersions] = useState(false)
  const [showVersions, setShowVersions] = useState(false)
  const [selectedVersion, setSelectedVersion] = useState<Processo | null>(null)
  
  // Estados separados para visualizações
  const [acessos, setAcessos] = useState<AcessoData[]>([])
  const [acessoCount, setAcessoCount] = useState<number>(0)
  const [loadingAcessos, setLoadingAcessos] = useState(false)
  const [showAcessos, setShowAcessos] = useState(false)

  // Usa a versão selecionada ou o processo principal
  const currentProcesso = selectedVersion || processo

  const processoAutorizacoes = Array.isArray(currentProcesso.autorizacoes)
    ? currentProcesso.autorizacoes
    : []
  const processoAutorizadores = Array.isArray(currentProcesso.autorizadores)
    ? currentProcesso.autorizadores
    : []

  const temAutorizacoes = processoAutorizacoes.length > 0
  const ehSequencial = temAutorizacoes && processoAutorizacoes.some(aut => aut.ordem !== null)

  const normalizeFlag = (value?: boolean | number | string | null) => {
    if (typeof value === 'string') {
      return value === '1' || value.toLowerCase() === 'true'
    }
    return value === true || value === 1
  }

  const fixedRoleAvailability: Record<FixedRoleKey, boolean> = {
    gestor_demandante: normalizeFlag(currentProcesso.gestor_demandante),
    executivo_demandante: normalizeFlag(currentProcesso.executivo_demandante),
    executivo_demandado: normalizeFlag(currentProcesso.executivo_demandado),
    gestor_demandado: normalizeFlag(currentProcesso.gestor_demandado),
  }

  const hasFixedRoles = Object.values(fixedRoleAvailability).some(Boolean)
  const shouldDisplayAuthorizations = temAutorizacoes || hasFixedRoles
  const displaySequencial = hasFixedRoles || ehSequencial

  const visualAutorizacoes: VisualAutorizacaoItem[] = []

  if (shouldDisplayAuthorizations) {
    let ordemCounter = 1
    const nextOrder = () => (displaySequencial ? ordemCounter++ : null)

    const pushFixedRole = (role: FixedRoleKey) => {
      visualAutorizacoes.push({
        id: `fixed-${role}-${ordemCounter}`,
        type: 'fixed',
        role,
        ordemDisplay: nextOrder(),
      })
    }

    const pushSetor = (setor: SetorAutorizador) => {
      visualAutorizacoes.push({
        id: `setor-${setor.id}-${ordemCounter}`,
        type: 'setor',
        setor,
        ordemDisplay: nextOrder(),
      })
    }

    ;(['gestor_demandante', 'executivo_demandante'] as FixedRoleKey[]).forEach(role => {
      if (fixedRoleAvailability[role]) {
        pushFixedRole(role)
      }
    })

    const autorizadorMap = new Map(
      processoAutorizadores.map(aut => [aut.id, aut as unknown as SetorAutorizador])
    )
    const sortedAutorizacoes = displaySequencial
      ? [...processoAutorizacoes].sort((a, b) => {
          const ordemA = a.ordem ?? Number.MAX_SAFE_INTEGER
          const ordemB = b.ordem ?? Number.MAX_SAFE_INTEGER
          return ordemA - ordemB
        })
      : processoAutorizacoes

    sortedAutorizacoes.forEach(aut => {
      const setorAutorizador = autorizadorMap.get(aut.autorizador_fk)
      if (!setorAutorizador) return
      pushSetor(setorAutorizador)
    })

    ;(['executivo_demandado', 'gestor_demandado'] as FixedRoleKey[]).forEach(role => {
      if (fixedRoleAvailability[role]) {
        pushFixedRole(role)
      }
    })
  }

  // Função para fazer download de anexo usando blob autenticado
  const handleDownloadAttachment = async (anexoObj: { 
    anexo: string; 
    nome_anexo: string; 
    usuario_criador_fk?: number 
  }) => {
    let link: HTMLAnchorElement | null = null;
    let downloadUrl: string | null = null;

    try {
      // Garantir que Sanctum esteja inicializado
      await authService.ensureSanctumInitialized();
      
      // O caminho do anexo de processo vem como "processos/anexos/arquivo.pdf"
      // Precisamos usar a rota correta: /storage/processos/anexos/{filename}
      const anexoPath = anexoObj.anexo;
      let endpoint: string;
      
      // Se o caminho contém "processos/anexos/", usar a rota específica
      if (anexoPath.includes('processos/anexos/')) {
        const filename = anexoPath.split('/').pop() || anexoPath;
        endpoint = `/storage/processos/anexos/${encodeURIComponent(filename)}`;
      } 
      // Se contém apenas "anexos/", usar a rota de demandas
      else if (anexoPath.includes('anexos/')) {
        const filename = anexoPath.split('/').pop() || anexoPath;
        endpoint = `/storage/anexos/${encodeURIComponent(filename)}`;
      }
      // Fallback: tentar extrair o nome do arquivo e usar rota de processos
      else {
        const filename = anexoPath.split('/').pop() || anexoPath;
        endpoint = `/storage/processos/anexos/${encodeURIComponent(filename)}`;
      }
      
      // Fazer requisição autenticada para baixar o blob
      const response = await api.get(endpoint, {
        responseType: 'blob',
        headers: {
          'Accept': '*/*'
        }
      });
      
      // Criar URL do blob e fazer download
      const blob = response.data;
      downloadUrl = URL.createObjectURL(blob);
      
      link = document.createElement('a');
      link.href = downloadUrl;
      link.download = anexoObj.nome_anexo;
      document.body.appendChild(link);
      link.click();
      
      // Aguardar um pouco antes de remover para garantir que o download iniciou
      await new Promise(resolve => setTimeout(resolve, 100));
      
    } catch (error) {
      console.error('Erro ao fazer download do anexo:', error);
      // Fallback: tentar método antigo
      const baseUrl = import.meta.env.VITE_API_URL;
      const anexoPath = anexoObj.anexo;
      const anexoUrl = anexoPath?.startsWith('http')
        ? anexoPath
        : `${baseUrl}/storage/${anexoPath}`;
      window.open(anexoUrl, '_blank');
    } finally {
      // Cleanup seguro: remove() não lança erro se o elemento não estiver no DOM
      link?.remove();
      
      // Limpar o objeto URL
      if (downloadUrl) {
        URL.revokeObjectURL(downloadUrl);
      }
    }
  };

  // Carregar versões do processo
  useEffect(() => {
    const loadVersions = async () => {
      if (!showVersions || versions.length > 0) return

      setLoadingVersions(true)
      try {
        const response = await processoService.getVersions(processo.id)
        setVersions(response.versions as Processo[])
      } catch (error) {
        console.error('Erro ao carregar versões:', error)
      } finally {
        setLoadingVersions(false)
      }
    }

    loadVersions()
  }, [showVersions, processo.id, versions.length])

  // Carregar acessos do processo (separado das versões)
  useEffect(() => {
    const loadAcessos = async () => {
      if (!showAcessos || !permissions.isAdmin || acessos.length > 0) return

      setLoadingAcessos(true)
      try {
        const response = await processoService.getVersions(processo.id)
        setAcessos(response.acessos || [])
        setAcessoCount(response.acessocount || 0)
      } catch (error) {
        console.error('Erro ao carregar acessos:', error)
      } finally {
        setLoadingAcessos(false)
      }
    }

    loadAcessos()
  }, [showAcessos, processo.id, permissions.isAdmin, acessos.length])

  return (
    <div className='p-4 sm:p-6'>
      {/* Timeline de Versões */}
      <div className='bg-gray-50 dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700 rounded-lg mb-4 p-3 sm:p-4'>
        <div className='flex items-center justify-between mb-3'>
          <div className='flex items-center gap-2'>
            <RiHistoryLine className='w-4 h-4 text-gray-600 dark:text-gray-400' />
            <h3 className='text-xs sm:text-sm font-semibold text-gray-900 dark:text-gray-100'>
              Histórico de Versões
            </h3>
          </div>
          <button
            onClick={() => setShowVersions(!showVersions)}
            className='text-xs text-blue-600 dark:text-blue-400 hover:underline font-medium'
          >
            {showVersions ? 'Ocultar' : 'Mostrar'}
          </button>
        </div>

        {showVersions && (
          <div className='mt-3'>
            {loadingVersions ? (
              <div className='flex items-center justify-center py-4'>
                <div className='animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 dark:border-blue-400'></div>
              </div>
            ) : versions.length > 0 ? (
              <HorizontalScrollNavigator
                showScrollbar
                className='h-auto'
                contentClassName='pb-2 scrollbar-hide'
                hideButtonsWhenDisabled={true}
                stepRatio={0.7}
              >
                <div className='flex gap-2.5 min-w-min'>
                  {versions.map((version, index) => {
                    const isSelected = selectedVersion?.id === version.id
                    const isCurrent = index === 0
                    
                    return (
                      <button
                        key={version.id}
                        onClick={() => setSelectedVersion(version)}
                        className={`flex-shrink-0 w-52 p-2.5 rounded-lg border transition-all ${
                          isSelected
                            ? 'bg-blue-50 dark:bg-blue-900/30 border-blue-500 dark:border-blue-500 ring-2 ring-blue-500/50 shadow-lg'
                            : isCurrent
                            ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 ring-2 ring-green-500/20 hover:shadow-md'
                            : 'bg-white dark:bg-neutral-700 border-gray-200 dark:border-neutral-600 hover:shadow-md hover:border-blue-300 dark:hover:border-blue-600'
                        }`}
                      >
                      {/* Header do card */}
                      <div className='flex items-center justify-between gap-1.5 mb-1.5'>
                        <div className='flex items-center gap-1.5'>
                          {isSelected ? (
                            <div className='flex items-center justify-center w-6 h-6 rounded-full bg-blue-600 dark:bg-blue-500'>
                              <span className='text-[10px] font-bold text-white'><HiEye/></span>
                            </div>
                          ) : isCurrent ? (
                            <div className='flex items-center justify-center w-6 h-6 rounded-full bg-green-600 dark:bg-green-500'>
                              <span className='text-[10px] font-bold text-white'><HiCheck/></span>
                            </div>
                          ) : (
                            <div className='flex items-center justify-center w-6 h-6 rounded-full bg-gray-200 dark:bg-neutral-600'>
                              <RiHistoryLine className='w-3.5 h-3.5 text-gray-600 dark:text-gray-400' />
                            </div>
                          )}
                          <span className='text-[11px] font-bold text-gray-900 dark:text-gray-100'>
                            V{versions.length - index}
                          </span>
                        </div>
                        {isSelected && (
                          <span className='inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold bg-blue-600 dark:bg-blue-500 text-white'>
                            VISUALIZANDO
                          </span>
                        )}
                        {isCurrent && !isSelected && (
                          <span className='inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold bg-green-600 dark:bg-green-500 text-white'>
                            ATUAL
                          </span>
                        )}
                      </div>

                      {/* Badge com ID */}
                      <div className='mb-1.5'>
                        <span className='inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-medium bg-gray-100 dark:bg-neutral-600 text-gray-700 dark:text-gray-300'>
                          ID: #{version.id}
                        </span>
                      </div>

                      {/* Nome do processo */}
                      <p className='text-[11px] text-gray-700 dark:text-gray-300 mb-1.5 line-clamp-2 min-h-[2rem] leading-tight'>
                        {version.nome_processo}
                      </p>

                        {/* Usuário que fez a alteração */}
                        {version.user_fk && version.usuario && (
                          <div className='flex items-center'>
                            <UserAvatar
                              user={version.usuario}
                              size='xs'
                              className='flex-shrink-0'
                            />
                            <div className='flex-1 min-w-0'>
                              <p className='text-[9px] text-gray-500 dark:text-gray-400'>
                                Alterado por
                              </p>
                              <p className='text-[10px] font-medium text-gray-900 dark:text-gray-100 truncate'>
                                {version.usuario.name}
                              </p>
                            </div>
                          </div>
                        )}

                      {/* Data de atualização */}
                      {version.updated_at && (
                        <div className='flex items-center gap-1 text-[9px] text-gray-500 dark:text-gray-400 mt-auto pt-1.5 border-t border-gray-200 dark:border-neutral-600'>
                          <RiCalendarLine className='w-2.5 h-2.5' />
                          <span>
                            {format(new Date(version.updated_at), "dd/MM/yy HH:mm")}
                          </span>
                        </div>
                      )}
                    </button>
                  )
                  })}
                </div>
              </HorizontalScrollNavigator>
            ) : (
              <p className='text-xs text-gray-500 dark:text-gray-400 italic text-center py-2'>
                Nenhuma versão anterior encontrada
              </p>
            )}
          </div>
        )}
      </div>

      {/* Seção de Visualizações - Apenas para ADMIN */}
      {permissions.isAdmin && (
        <div className='bg-gray-50 dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700 rounded-lg mb-4 p-3 sm:p-4'>
          <div className='flex items-center justify-between mb-3'>
            <div className='flex items-center gap-2'>
              <RiEyeLine className='w-4 h-4 text-blue-600 dark:text-blue-400' />
              <h3 className='text-xs sm:text-sm font-semibold text-gray-900 dark:text-gray-100'>
                Visualizações do Processo
              </h3>
              {acessoCount > 0 && (
                <span className='inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-400'>
                  {acessoCount}
                </span>
              )}
            </div>
            <button
              onClick={() => setShowAcessos(!showAcessos)}
              className='text-xs text-blue-600 dark:text-blue-400 hover:underline font-medium'
            >
              {showAcessos ? 'Ocultar' : 'Mostrar'}
            </button>
          </div>

          {showAcessos && (
            <div className='mt-3 overflow-y-auto max-h-64'>
              {loadingAcessos ? (
                <div className='flex items-center justify-center py-4'>
                  <div className='animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 dark:border-blue-400'></div>
                </div>
              ) : acessos.length > 0 ? (
                <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2'>
                  {acessos.map((acesso) => (
                    <div
                      key={acesso.id}
                      className='flex items-center gap-2.5 p-2 rounded-lg bg-white dark:bg-neutral-700 border border-gray-200 dark:border-neutral-600'
                    >
                      {acesso.usuario && (
                        <>
                          <UserAvatar
                            user={{
                              ...acesso.usuario,
                              funcao_usuario: '',
                              cargo_usuario: ''
                            }}
                            size='sm'
                            className='flex-shrink-0'
                          />
                          <div className='flex-1 min-w-0'>
                            <p className='text-xs font-medium text-gray-900 dark:text-gray-100 truncate'>
                              {acesso.usuario.name}
                            </p>
                            <p className='text-xs text-gray-500 dark:text-gray-400 truncate'>
                              {format(new Date(acesso.created_at), 'dd/MM/yy HH:mm')}
                            </p>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className='text-xs text-gray-500 dark:text-gray-400 italic text-center py-2'>
                  Nenhuma visualização encontrada para este processo
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Layout Responsivo - Grid Principal */}
      <div className='grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6'>
        {/* Coluna Esquerda */}
        <div className='space-y-4 sm:space-y-5'>
          {/* Informações Básicas */}
          <div className='bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 sm:p-4'>
            <div className='flex items-center gap-2 mb-3'>
              <RiFilePaper2Line className='w-4 h-4 sm:w-5 sm:h-5 text-blue-600 dark:text-blue-400' />
              <h3 className='text-xs sm:text-sm font-semibold text-gray-900 dark:text-gray-100'>
                Informações Básicas
              </h3>
            </div>

            {/* Setor */}
            {currentProcesso.setor && (
              <div className='mb-3'>
                <label className='text-xs font-medium text-gray-600 dark:text-gray-400 block mb-1'>
                  Setor Responsável
                </label>
                {onNavigateToSetor ? (
                  <button
                    onClick={() => onNavigateToSetor(currentProcesso.setor!)}
                    className='flex items-center gap-2 p-2 rounded-lg bg-white dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700 hover:border-blue-300 dark:hover:border-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all w-full text-left group'
                  >
                    <HiOutlineBuildingOffice className='w-4 h-4 text-gray-400 group-hover:text-blue-600 dark:group-hover:text-blue-400' />
                    <div className='flex-1 min-w-0'>
                      <p className='text-xs sm:text-sm font-medium text-gray-900 dark:text-gray-100 group-hover:text-blue-600 dark:group-hover:text-blue-400 truncate'>
                        {currentProcesso.setor.nome_setor}
                      </p>
                      {currentProcesso.setor.chefe && (
                        <p className='text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1 truncate'>
                          <UserAvatar
                            user={currentProcesso.setor.chefe}
                            size='xs'
                            className='flex-shrink-0'
                          />
                          Chefe: {currentProcesso.setor.chefe.name}
                        </p>
                      )}
                    </div>
                  </button>
                ) : (
                  <div className='flex items-center gap-2 p-2 rounded-lg bg-white dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700'>
                    <HiOutlineBuildingOffice className='w-4 h-4 text-gray-400 flex-shrink-0' />
                    <div className='min-w-0 flex-1'>
                      <p className='text-xs sm:text-sm font-medium text-gray-900 dark:text-gray-100 truncate'>
                        {currentProcesso.setor.nome_setor}
                      </p>
                      {currentProcesso.setor.chefe && (
                        <p className='text-xs text-gray-500 dark:text-gray-400 truncate'>
                          Chefe: {currentProcesso.setor.chefe.name}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Descrição */}
            <div>
              <label className='text-xs font-medium text-gray-600 dark:text-gray-400 block mb-1'>
                Descrição
              </label>
              <div
                className='text-xs sm:text-sm text-gray-700 dark:text-gray-300 leading-relaxed rich-text-content'
                dangerouslySetInnerHTML={{ __html: processRichTextForDisplay(currentProcesso.descricao || '') }}
              />
            </div>
          </div>

          {/* Prazos por Prioridade */}
          <div className='bg-white dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700 rounded-lg p-3 sm:p-4'>
            <div className='flex items-center gap-2 mb-3'>
              <HiClock className='w-4 h-4 sm:w-5 sm:h-5 text-gray-600 dark:text-gray-400' />
              <h3 className='text-xs sm:text-sm font-semibold text-gray-900 dark:text-gray-100'>
                Prazos por Prioridade
              </h3>
            </div>
            <div className='grid grid-cols-1 sm:grid-cols-2 gap-2'>
              <PriorityBadge type='baixa' days={currentProcesso.prioridade_baixa} />
              <PriorityBadge type='media' days={currentProcesso.prioridade_media} />
              <PriorityBadge type='alta' days={currentProcesso.prioridade_alta} />
              <PriorityBadge type='urgente' days={currentProcesso.prioridade_urgente} />
            </div>
          </div>
        </div>

        {/* Coluna Direita */}
        <div className='space-y-4 sm:space-y-5'>
          {/* Instruções */}
          {currentProcesso.instrucoes && currentProcesso.instrucoes.trim() ? (
            <div className='bg-white dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700 rounded-lg p-3 sm:p-4'>
              <div className='flex items-center gap-2 mb-3'>
                <FaBook className='w-4 h-4 text-blue-600 dark:text-blue-400' />
                <h3 className='text-xs sm:text-sm font-semibold text-gray-900 dark:text-gray-100'>
                  Instruções
                </h3>
              </div>
              <div
                className='text-xs sm:text-sm text-gray-700 dark:text-gray-300 leading-relaxed rich-text-content'
                dangerouslySetInnerHTML={{ __html: processRichTextForDisplay(currentProcesso.instrucoes || '') }}
              />
            </div>
          ) : (
            <div className='bg-gray-50 dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700 rounded-lg p-3 sm:p-4'>
              <div className='flex items-center gap-2 mb-2'>
                <FaBook className='w-4 h-4 text-gray-400' />
                <h3 className='text-xs sm:text-sm font-semibold text-gray-900 dark:text-gray-100'>
                  Instruções
                </h3>
              </div>
              <p className='text-xs sm:text-sm text-gray-500 dark:text-gray-400 italic'>
                Nenhuma instrução de preenchimento cadastrada para este processo
              </p>
            </div>
          )}

          {/* Template de Formulário */}
          {currentProcesso.template_schema ? (
            <div className='bg-gradient-to-br from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-3 sm:p-4'>
              <div className='flex items-center gap-2 mb-3'>
                <RiFileList3Line className='w-4 h-4 sm:w-5 sm:h-5 text-purple-600 dark:text-purple-400' />
                <h3 className='text-xs sm:text-sm font-semibold text-gray-900 dark:text-gray-100'>
                  Template de Formulário
                </h3>
                <span className='inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-400'>
                  Ativo
                </span>
              </div>
              
              {/* Informações do Template */}
              <div className='space-y-3'>
                <div className='flex items-center gap-3 p-2.5 rounded-lg bg-white dark:bg-neutral-800 border border-purple-200 dark:border-purple-700'>
                  <div className='flex-shrink-0 w-10 h-10 rounded-md bg-purple-100 dark:bg-purple-900/40 flex items-center justify-center'>
                    <RiFileList3Line className='w-5 h-5 text-purple-600 dark:text-purple-400' />
                  </div>
                  <div className='flex-1 min-w-0'>
                    <p className='text-xs sm:text-sm font-semibold text-gray-900 dark:text-gray-100'>
                      {currentProcesso.template_schema.template_nome}
                    </p>
                    <div className='flex items-center gap-2 mt-0.5'>
                      <span className='text-xs text-gray-500 dark:text-gray-400'>
                        ID: {currentProcesso.template_schema.template_id}
                      </span>
                      {currentProcesso.template_schema.versao && (
                        <>
                          <span className='text-gray-300 dark:text-gray-600'>•</span>
                          <span className='text-xs text-gray-500 dark:text-gray-400'>
                            v{currentProcesso.template_schema.versao}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Campos do Formulário */}
                {currentProcesso.template_schema.schema?.properties && (
                  <div>
                    <p className='text-xs font-medium text-gray-600 dark:text-gray-400 mb-2'>
                      Campos do formulário ({Object.keys(currentProcesso.template_schema.schema.properties).length})
                    </p>
                    <div className='grid grid-cols-1 sm:grid-cols-2 gap-1.5'>
                      {Object.entries(currentProcesso.template_schema.schema.properties).map(([key, field]) => {
                        const fieldDef = field as { title?: string; type?: string }
                        const isRequired = Array.isArray(currentProcesso.template_schema?.schema?.required) && 
                                          currentProcesso.template_schema.schema.required.includes(key)
                        return (
                          <div 
                            key={key}
                            className='flex items-center gap-2 px-2.5 py-1.5 rounded-md bg-gray-50 dark:bg-neutral-700/50 border border-gray-200 dark:border-neutral-600'
                          >
                            <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${isRequired ? 'bg-red-500' : 'bg-gray-400'}`}></div>
                            <span className='text-xs text-gray-700 dark:text-gray-300 truncate'>
                              {fieldDef.title || key}
                            </span>
                            {isRequired && (
                              <span className='text-xs text-red-500 dark:text-red-400 ml-auto flex-shrink-0'>*</span>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )}

                {/* Configurações de Fluxo Simplificado */}
                {currentProcesso.template_schema.fluxoSimplificado?.ativo && (
                  <div className='mt-3 pt-3 border-t border-purple-200 dark:border-purple-700'>
                    <p className='text-xs font-medium text-gray-600 dark:text-gray-400 mb-2'>
                      Configurações de Fluxo Simplificado
                    </p>
                    <div className='flex flex-wrap gap-1.5'>
                      <span className='inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400'>
                        ✓ Fluxo Simplificado
                      </span>
                      {currentProcesso.template_schema.fluxoSimplificado.demandanteAutomatico && (
                        <span className='inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-400'>
                          Demandante Automático
                        </span>
                      )}
                      {currentProcesso.template_schema.fluxoSimplificado.tituloAutomatico && (
                        <span className='inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-400'>
                          Título Automático
                        </span>
                      )}
                      {currentProcesso.template_schema.fluxoSimplificado.prioridadePadrao && (
                        <span className='inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-400'>
                          Prioridade: {currentProcesso.template_schema.fluxoSimplificado.prioridadePadrao}
                        </span>
                      )}
                      {currentProcesso.template_schema.fluxoSimplificado.diasEntregaPadrao && (
                        <span className='inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-gray-100 dark:bg-gray-900/30 text-gray-800 dark:text-gray-400'>
                          Prazo: {currentProcesso.template_schema.fluxoSimplificado.diasEntregaPadrao} dias
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className='bg-gray-50 dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700 rounded-lg p-3 sm:p-4'>
              <div className='flex items-center gap-2 mb-2'>
                <RiFileList3Line className='w-4 h-4 text-gray-400' />
                <h3 className='text-xs sm:text-sm font-semibold text-gray-900 dark:text-gray-100'>
                  Template de Formulário
                </h3>
              </div>
              <p className='text-xs sm:text-sm text-gray-500 dark:text-gray-400 italic'>
                Este processo não utiliza template de formulário dinâmico
              </p>
            </div>
          )}

          {/* Links Úteis */}
          {Array.isArray(currentProcesso.links) && currentProcesso.links.length > 0 ? (
            <div className='bg-white dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700 rounded-lg p-3 sm:p-4'>
              <div className='flex items-center gap-2 mb-3'>
                <RiLinksLine className='w-4 h-4 text-blue-600 dark:text-blue-400' />
                <h3 className='text-xs sm:text-sm font-semibold text-gray-900 dark:text-gray-100'>
                  Links Úteis ({currentProcesso.links.length})
                </h3>
              </div>
              <div className='space-y-2'>
                {currentProcesso.links.map((linkObj, index) => {
                  const url = typeof linkObj === 'string' ? linkObj : linkObj.link
                  const titulo =
                    typeof linkObj === 'object' && linkObj.titulo ? linkObj.titulo : null
                  return (
                    <a
                      key={index}
                      href={url}
                      target='_blank'
                      rel='noopener noreferrer'
                      className='flex items-center gap-2 p-2.5 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors group'
                    >
                      <div className='flex-shrink-0 w-10 h-10 rounded-md bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center'>
                        <RiLinksLine className='w-5 h-5 text-blue-600 dark:text-blue-400' />
                      </div>
                      <div className='flex-1 min-w-0'>
                        {titulo ? (
                          <>
                            <p className='text-xs sm:text-sm font-semibold text-blue-900 dark:text-blue-100 group-hover:underline truncate'>
                              {titulo}
                            </p>
                            <p className='text-xs text-blue-600 dark:text-blue-400 truncate'>
                              {url}
                            </p>
                          </>
                        ) : (
                          <p className='text-xs sm:text-sm font-medium text-blue-900 dark:text-blue-300 group-hover:underline truncate'>
                            {url}
                          </p>
                        )}
                      </div>
                      <span className='flex-shrink-0 text-lg text-blue-500 dark:text-blue-400 opacity-0 group-hover:opacity-100 transition-opacity'>
                        ↗
                      </span>
                    </a>
                  )
                })}
              </div>
            </div>
          ) : (
            <div className='bg-gray-50 dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700 rounded-lg p-3 sm:p-4'>
              <div className='flex items-center gap-2 mb-2'>
                <RiLinksLine className='w-4 h-4 text-gray-400' />
                <h3 className='text-xs sm:text-sm font-semibold text-gray-900 dark:text-gray-100'>
                  Links Úteis
                </h3>
              </div>
              <p className='text-xs sm:text-sm text-gray-500 dark:text-gray-400 italic'>
                Nenhum link útil cadastrado para este processo
              </p>
            </div>
          )}

          {/* Anexos */}
          {Array.isArray(currentProcesso.anexos) && currentProcesso.anexos.length > 0 ? (
            <div className='bg-white dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700 rounded-lg p-3 sm:p-4'>
              <div className='flex items-center gap-2 mb-3'>
                <RiAttachmentLine className='w-4 h-4 text-purple-600 dark:text-purple-400' />
                <h3 className='text-xs sm:text-sm font-semibold text-gray-900 dark:text-gray-100'>
                  Anexos ({currentProcesso.anexos.length})
                </h3>
              </div>
              <div className='space-y-2'>
                {currentProcesso.anexos.map(
                  (
                    anexoObj: { anexo: string; nome_anexo: string; usuario_criador_fk?: number },
                    index: number
                  ) => {
                    // Extrair nome e extensão do arquivo
                    const fileName =
                      anexoObj.nome_anexo || anexoObj.anexo?.split('/').pop() || `Anexo ${index + 1}`
                    const fileExtension = fileName.split('.').pop()?.toUpperCase() || 'FILE'

                    return (
                      <div
                        key={index}
                        className='flex items-center justify-between p-2.5 rounded-lg bg-white dark:bg-neutral-700/50 border border-gray-200 dark:border-neutral-600 hover:shadow-sm dark:hover:bg-neutral-700 transition-all group'
                      >
                        <div className='flex items-center gap-2.5 flex-1 min-w-0'>
                          <div
                            className={`flex-shrink-0 w-10 h-10 rounded-md flex items-center justify-center ${getFileIconBgColor(fileName)}`}
                          >
                            {getFileIcon(fileName)}
                          </div>
                          <div className='flex-1 min-w-0'>
                            <p className='text-xs sm:text-sm font-medium text-gray-900 dark:text-gray-100 truncate'>
                              {fileName}
                            </p>
                            <p className='text-xs text-gray-500 dark:text-gray-400'>
                              {fileExtension}
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => handleDownloadAttachment(anexoObj)}
                          className='flex-shrink-0 ml-2 p-2 rounded-md bg-purple-600 dark:bg-purple-700 hover:bg-purple-700 dark:hover:bg-purple-600 text-white transition-colors shadow-sm hover:shadow'
                          title='Baixar arquivo'
                        >
                          <RiDownloadLine className='w-4 h-4' />
                        </button>
                      </div>
                    )
                  }
                )}
              </div>
            </div>
          ) : (
            <div className='bg-gray-50 dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700 rounded-lg p-3 sm:p-4'>
              <div className='flex items-center gap-2 mb-2'>
                <RiAttachmentLine className='w-4 h-4 text-gray-400' />
                <h3 className='text-xs sm:text-sm font-semibold text-gray-900 dark:text-gray-100'>
                  Anexos
                </h3>
              </div>
              <p className='text-xs sm:text-sm text-gray-500 dark:text-gray-400 italic'>
                Nenhum arquivo anexado a este processo
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Seções de largura total - abaixo das colunas */}
      <div className='space-y-4 mt-4 sm:space-y-5'>
        {/* Autorizações */}
        {shouldDisplayAuthorizations ? (
          <div className='bg-white dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700 rounded-lg p-3 sm:p-4'>
            <div className='flex items-center gap-2 mb-3'>
              <FaLock className='w-4 h-4 sm:w-5 sm:h-5 text-blue-600 dark:text-blue-400' />
              <h3 className='text-xs sm:text-sm font-semibold text-gray-900 dark:text-gray-100'>
                Autorizações Requeridas
              </h3>
            </div>

            <div className='mb-3'>
              <span
                className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                  displaySequencial
                    ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-400'
                    : 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400'
                }`}
              >
                {displaySequencial ? 'Sequencial' : 'Paralelo'}
              </span>
            </div>

            <div className='grid grid-cols-1 md:grid-cols-2 gap-2'>
              {visualAutorizacoes.map(item => {
                if (item.type === 'setor') {
                  const setorAutorizador = item.setor
                  const CardContent = (
                    <>
                      {displaySequencial && item.ordemDisplay !== null && (
                        <div className='w-6 h-6 rounded-full bg-blue-600 dark:bg-blue-500 text-white flex items-center justify-center font-semibold text-xs flex-shrink-0'>
                          {item.ordemDisplay}
                        </div>
                      )}
                      <div className='w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0'>
                        <HiOutlineBuildingOffice className='w-4 h-4 text-blue-600 dark:text-blue-400' />
                      </div>
                      <div className='flex-1 min-w-0'>
                        <p className='text-xs sm:text-sm font-medium text-gray-900 dark:text-gray-100 truncate group-hover:text-blue-600 dark:group-hover:text-blue-400'>
                          {setorAutorizador.nome_setor}
                        </p>
                        <p className='text-xs text-gray-600 dark:text-gray-400 truncate'>
                          {setorAutorizador.equipe ? 'Equipe' : 'Setor'}
                        </p>
                      </div>
                    </>
                  )

                  return onNavigateToSetor ? (
                    <button
                      key={item.id}
                      onClick={() => onNavigateToSetor(setorAutorizador as unknown as Setor)}
                      className='flex items-center gap-3 p-2 rounded-lg bg-gray-50 dark:bg-neutral-700 hover:bg-blue-50 dark:hover:bg-blue-900/20 border border-gray-200 dark:border-neutral-700 hover:border-blue-300 dark:hover:border-blue-600 transition-all w-full text-left group'
                    >
                      {CardContent}
                    </button>
                  ) : (
                    <div
                      key={item.id}
                      className='flex items-center gap-3 p-2 rounded-lg bg-gray-50 dark:bg-neutral-700 border border-gray-200 dark:border-neutral-700 group'
                    >
                      {CardContent}
                    </div>
                  )
                }

                const meta = FIXED_ROLE_META[item.role]
                return (
                  <div
                    key={item.id}
                    className='flex items-center gap-3 p-2 rounded-lg bg-gray-50 dark:bg-neutral-700 border border-dashed border-gray-200 dark:border-neutral-600'
                  >
                    {displaySequencial && item.ordemDisplay !== null && (
                      <div className='w-6 h-6 rounded-full bg-blue-600 dark:bg-blue-500 text-white flex items-center justify-center font-semibold text-xs flex-shrink-0'>
                        {item.ordemDisplay}
                      </div>
                    )}
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${meta.bubbleClass}`}>
                      <span className='text-[10px] font-bold text-white drop-shadow-sm'>{meta.initials}</span>
                    </div>
                    <div className='flex-1 min-w-0'>
                      <p className='text-xs sm:text-sm font-semibold text-gray-900 dark:text-gray-100 truncate'>
                        {meta.label}
                      </p>
                      <p className='text-[11px] text-gray-600 dark:text-gray-400 leading-snug'>
                        {meta.description}
                      </p>
                      <span className='inline-flex items-center px-2 py-0.5 mt-1 rounded-full text-[10px] font-semibold bg-gray-200/70 dark:bg-neutral-600 text-gray-700 dark:text-gray-100'>
                        Papel fixo
                      </span>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        ) : (
          <div className='bg-gray-50 dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700 rounded-lg p-3 sm:p-4'>
            <div className='flex items-center gap-2 text-gray-500 dark:text-gray-400'>
              <FaLock className='w-4 h-4' />
              <p className='text-xs sm:text-sm italic'>Este processo não requer autorização</p>
            </div>
          </div>
        )}

        {/* Informações de Auditoria */}
        <div className='bg-gray-50 dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700 rounded-lg p-3 sm:p-4'>
          <div className='flex items-center gap-2 mb-3'>
            <RiCalendarLine className='w-4 h-4 text-gray-600 dark:text-gray-400' />
            <h3 className='text-xs sm:text-sm font-semibold text-gray-900 dark:text-gray-100'>
              Informações de Auditoria
            </h3>
          </div>
          <div className='grid grid-cols-2 lg:grid-cols-4 gap-3 text-xs'>
            <div>
              <p className='text-gray-500 dark:text-gray-400 mb-0.5'>ID do Processo</p>
              <p className='font-mono font-semibold text-gray-900 dark:text-gray-100'>
                #{currentProcesso.id}
              </p>
            </div>
            <div>
              <p className='text-gray-500 dark:text-gray-400 mb-0.5'>Status</p>
              <span className='inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400'>
                Ativo
              </span>
            </div>
            {currentProcesso.created_at && (
              <div>
                <p className='text-gray-500 dark:text-gray-400 mb-0.5'>Criado em</p>
                <p className='font-medium text-gray-900 dark:text-gray-100 text-xs'>
                  {format(new Date(currentProcesso.created_at!), "dd/MM/yyyy 'às' HH:mm")}
                </p>
              </div>
            )}
            {/* {processo.updated_at && (
              <div>
                <p className='text-gray-500 dark:text-gray-400 mb-0.5'>Atualizado em</p>
                <p className='font-medium text-gray-900 dark:text-gray-100 text-xs'>
                  {format(new Date(processo.updated_at!), "dd/MM/yyyy 'às' HH:mm")}
                </p>
              </div>
            )} */}
          </div>
        </div>
      </div>
    </div>
  )
}
